// A vendor's payouts (aldoo-na store/payouts) — the same panel in the vendor
// portal (Payouts) and on the vendor's page in the admin (Payouts tab): the
// share, the next payout, what is held, what was paid (under the progress to the
// minimum payout), and the payouts made. The sales, with the commission and the
// vendor's earnings, are in Sales (AdminVendors/SalesTab); who is paid, the
// bank account and the contract in the Company section (Payouts/CompanyParts,
// Payouts/ContractTab).
// The admin also pays a vendor now, marks a bank transfer made, or a payout failed.
// A posted payout carries its statement and commission invoice (opened in the PDF
// viewer), Stripe's receipt and the bank's trace id (Payouts/InvoicePdf).
import React, { useCallback, useEffect, useRef, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { showSuccessToast, showErrorToast } from "aldoo-ra/Toast"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { useUrlParam } from "aldoo-ra/URLState"
import { Section, Empty, Chip, TextInput, LoadError, money, dateOnly } from "../AdminShared/ui"
import { PayoutsOverview, PayoutsAdmin } from "../AdminShared/api"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { PAYOUTS } from "../Vendor/texts/bundles"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import useSubmissionEvents from "../Vendor/useSubmissionEvents"
import useStoreEvents from "aldoo-ra/Store/useStoreEvents"
import { REASON } from "./PayoutRuns"
import { InvoiceViewer, openReceipt } from "./InvoicePdf"
import DataTable from "aldoo-ra/DataTable"
import Tabs from "aldoo-ra/Tabs"
import { CommissionTab, BillingInvoiceViewer } from "./BillingTabs"
import { CalendarDaysIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// the help center's article on the payouts (aldoo-com-api scripts/help-center/vendor/articles/payouts.html): its sections, by id
const HELP = { all: "vendor.payouts", next: "vendor.payouts#next", minimum: "vendor.payouts#minimum", held: "vendor.payouts#held", paid: "vendor.payouts#paid", share: "vendor.payouts#share", list: "vendor.payouts#list" }

const PAYOUT_CHIP = { created: "bg-admin_primary/10 text-admin_primary", processing: "bg-admin_progress/10 text-admin_progress", posted: "bg-admin_success/10 text-admin_success", failed: "bg-admin_error/10 text-admin_error", returned: "bg-admin_error/10 text-admin_error", canceled: "bg-admin_neutral text-admin_text_secondary" }
const PAYOUT_LABEL = { created: "Being sent", processing: "Being sent", posted: "Paid", failed: "Failed", returned: "Returned by the bank", canceled: "Canceled" }
// the unsigned contract is the checklist's to say (Signed payout contract), not a notice here
const BLOCKER = { payout_details_incomplete: "Payouts start once your payout details are complete." }

/**
 * What the vendor will get, large, as a payment app shows a balance: once over the contract's
 * minimum, the next payout and its day; below it, what is accumulated. Under it, always, the way
 * to the minimum (a bar, the share of it reached, the minimum) and the last payout made.
 * `action`: the admin's Pay now, at the card's side.
 */
function NextPayoutHero({ nextPayout, unpaid, minimum, currency, nextDate, last, locale, action, noContract, t }) {
  // with no signed contract nothing is paid: what is accumulated waits for it
  const below = noContract || (minimum > 0 && nextPayout < minimum)
  const amount = below ? Math.max(0, unpaid) : nextPayout
  const reached = minimum > 0 ? Math.max(0, Math.min(100, Math.floor((Math.max(0, unpaid) / minimum) * 100))) : 100
  return (
    <div className="rounded-2xl border border-admin_bordered bg-admin_paper px-6 py-6 shadow-[0_4px_20px_rgba(0,0,0,0.06)] flex flex-col gap-5" data-testid="payouts-next">
      <div className="flex flex-col-reverse sm:flex-row sm:items-start justify-between gap-3 sm:gap-6">
        <div className="flex flex-col gap-1 min-w-0">
          <Typography variant="admin_h4" className="inline-flex items-center gap-1.5">
            {below ? t`Accumulated` : t("You will receive on %s", nextDate)}
            <HelpHint helpKey={HELP.next} />
          </Typography>
          <Typography variant="admin_txt1_dim">
            {noContract
              ? t`Paid once your payout contract is signed.`
              : below
                ? t("Below %s, your sales wait for the next payout.", money(minimum, currency))
                : t`to your bank account, for the sales due by then`}
          </Typography>
        </div>

        <div className="flex flex-col gap-2 sm:items-end shrink-0">
          <Typography 
            component="output"
            variant="h1"
            className="text-3xl sm:text-4xl lg:text-6xl font-light tracking-tight tabular-nums leading-none" 
            data-testid="payouts-next-amount"
          >
            {money(amount, currency)}
          </Typography>
          {action}
        </div>
      </div>
      {minimum > 0 && (
        <div className="flex flex-col gap-1.5" data-testid="payouts-minimum">
          <div className="h-2.5 w-full rounded-full bg-admin_neutral_1 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${below ? "bg-admin_primary" : "bg-admin_success"}`} style={{ width: `${reached}%` }} />
          </div>
          <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-xs text-admin_text_secondary">
            <span>{t("You have reached %s%% of the minimum payout", reached)}</span>
            <span className="inline-flex items-center gap-1">{t("Minimum payout: %s", money(minimum, currency))}<HelpHint helpKey={HELP.minimum} /></span>
          </div>
        </div>
      )}
      {last && (
        <div className="flex items-center gap-2 text-sm" data-testid="payouts-last">
          <CalendarDaysIcon className="w-5 h-5 text-admin_text_secondary shrink-0" />
          {t("Your last payout was made on %s for %s.", dateOnly(last.postedAt || last.runAt || last.createdAt, locale), money(last.amount, last.currency))}
        </div>
      )}
    </div>
  )
}

function Card({ label, value, sub, testId, helpKey }) {
  return (
    <div className="rounded-xl border border-admin_bordered bg-admin_paper px-4 py-3" data-testid={testId}>
      <Typography variant="admin_label_sm_dim" className="flex items-center gap-1">
        {label}
        {helpKey && <HelpHint helpKey={helpKey} />}
      </Typography>
      <Typography 
        component="output"
        variant="admin_h4" 
        className="tabular-nums"
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="admin_txt2_dim" className="block mt-0.5">
          {sub}
        </Typography>
      )}
    </div>
  )
}

/**
 * One payout, as the buyer's orders are listed: closed, its date, where it stands and the amount
 * (a click opens it); open, a card with two tabs — its Report (the PDF, Stripe's receipt) and its
 * Stats (sales, refunds, commission, corrections, where it went and with what message, the
 * references) — and, for the admin, the transfer's last step while it waits.
 */
function PayoutItem({ payout, admin, locale, t, expanded, dimmed, initialTab, onToggle, onChanged }) {
  const [reference, setReference] = useState("")
  const hasReport = !!payout.invoiceNumber
  // the cash on delivery commission's invoice and report (a payout it was kept back from), open in the viewer
  const commissionDoc = payout.commissionInvoice ? { _id: payout.commissionInvoice._id, number: payout.commissionInvoice.number, stripe: payout.commissionInvoice.stripeNumber ? { number: payout.commissionInvoice.stripeNumber } : null } : null
  const [doc, setDoc] = useState(null)
  const [tab, setTab] = useState(initialTab || (hasReport ? "report" : "stats"))
  useEffect(() => {
    if (expanded) setTab(initialTab || (hasReport ? "report" : "stats"))
  }, [expanded]) // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (op, extra) => {
    const res = await PayoutsAdmin(op, { payoutId: payout._id, ...extra }).catch((e) => ({ error: e?.message }))
    if (res?.error) return showErrorToast(res.error)
    onChanged?.()
  }
  const date = dateOnly(payout.postedAt || payout.runAt || payout.createdAt, locale)
  const head = (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="sm:w-[130px] font-medium">{date}</span>
        <Chip className={PAYOUT_CHIP[payout.status] || "bg-admin_neutral_1"}>
          {t(PAYOUT_LABEL[payout.status] || payout.status)}
        </Chip>
        <span className="font-bold tabular-nums" data-testid="payout-amount">
          {money(payout.amount, payout.currency)}
        </span>
      </div>
      <div className="flex items-center gap-4">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-admin_text_secondary group-hover:text-admin_text">
          {t`Details`}
          {expanded ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
        </span>
      </div>
    </>
  )
  if (!expanded)
    return (
      <div
        role="button"
        tabIndex={0}
        aria-expanded={false}
        onClick={onToggle}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onToggle())}
        className={`group flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3.5 cursor-pointer transition-opacity ${dimmed ? "opacity-40 hover:opacity-100" : ""}`}
        data-testid="payout-row"
      >
        {head}
      </div>
    )

  const stripeId = payout.providerPaymentId || (/^obp_/.test(payout.reference || "") ? payout.reference : "")
  // the figures of the payout, a label and a value each
  const stats = [
    [t`Sales`, money(payout.gross, payout.currency)],
    [t`Refunds`, `−${money(payout.refunded, payout.currency)}`],
    payout.commission < 0 ? [t`Commission returned`, money(-payout.commission, payout.currency)] : [t`Aldoo commission`, `−${money(payout.commission, payout.currency)}`],
    payout.adjustments ? [t`Refunds after earlier payouts`, money(payout.adjustments, payout.currency)] : null,
    [t`Amount paid out`, money(payout.amount, payout.currency), true],
    [t`Sales in this payout`, payout.earningsCount ? String(payout.earningsCount) : "—"],
    payout.ibanLast4 ? [t`Paid to account`, `IBAN •••• ${payout.ibanLast4}`] : null,
    payout.stripeAccountId ? [t`Paid to account`, `Stripe ${payout.stripeAccountId}`] : null,
    payout.transferMessage ? [t`Transfer message`, payout.transferMessage] : null,
    // a Stripe payment's id given as the reference (obp_…) is the Stripe payment
    payout.reference && payout.reference !== stripeId ? [t`Bank reference`, payout.reference] : null,
    stripeId ? [t`Stripe payment`, stripeId] : null,
    payout.traceId ? [t`Trace ID`, payout.traceId] : null,
    payout.failureReason ? [t`Why it failed`, payout.failureReason, false, true] : null,
  ].filter(Boolean)
  const tabs = [hasReport && ["report", t`Report`], ["stats", t`Stats`]].filter(Boolean)
  return (
    <div className="my-2 rounded-xl bg-admin_paper px-4 py-4 shadow-[0_6px_24px_rgba(0,0,0,0.10)] flex flex-col gap-3" data-testid="payout-row">
      <div role="button" tabIndex={0} aria-expanded onClick={onToggle} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onToggle())} className="group flex flex-wrap items-center justify-between gap-x-4 gap-y-2 cursor-pointer" data-testid="payout-collapse">
        {head}
      </div>
      <div className="flex gap-6 border-b border-admin_bordered" role="tablist">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`-mb-px pb-2 border-b-2 text-sm font-semibold ${tab === id ? "border-admin_primary text-admin_text" : "border-transparent text-admin_text_secondary hover:text-admin_text"}`}
            data-testid={`payout-tab-${id}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "report" && hasReport && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="font-semibold">{t("Payout report No. %s", payout.invoiceNumber)}</span>
            {payout.receiptUrl && (
              <button
                type="button"
                className="font-medium text-admin_primary hover:underline"
                onClick={() => openReceipt(payout)}
                data-testid="payout-receipt"
              >
                {t`Stripe receipt`} ↗
              </button>
            )}
            {/* the cash on delivery commission kept back from it: its invoice (Stripe's) and report */}
            {commissionDoc?.stripe?.number && (
              <button
                type="button"
                className="font-medium text-admin_primary hover:underline"
                onClick={() => setDoc({ invoice: commissionDoc, which: "stripe" })}
                data-testid="payout-commission-invoice"
              >
                {t("Commission invoice No. %s", commissionDoc.stripe.number)}
              </button>
            )}
            {commissionDoc && (
              <button
                type="button"
                className="font-medium text-admin_primary hover:underline"
                onClick={() => setDoc({ invoice: commissionDoc, which: null })}
                data-testid="payout-commission-report"
              >
                {t("Report No. %s", commissionDoc.number)}
              </button>
            )}
          </div>
          {doc && <BillingInvoiceViewer orgId={payout.orgId} invoice={doc.invoice} which={doc.which} onClose={() => setDoc(null)} />}
          <InvoiceViewer payoutId={payout._id} t={t} height="60vh" />
        </div>
      )}
      {tab === "stats" && (
        <dl className="m-0 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm" data-testid="payout-stats">
          {stats.map(([label, value, strong, error]) => (
            <div key={label} className={`flex justify-between gap-3 border-b border-admin_bordered/50 py-1 ${strong ? "font-semibold" : ""}`}>
              <dt className="text-admin_text_secondary">{label}</dt>
              <dd className={`m-0 text-right break-all ${error ? "text-admin_error" : ""}`}>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {admin && ["created", "processing"].includes(payout.status) && (
        <div className="flex flex-wrap items-center gap-2 border-t border-admin_bordered/60 pt-3">
          {payout.method === "manual" && <TextInput value={reference} onChange={setReference} placeholder="Bank / Stripe reference (optional)" className="w-[240px]" />}
          <Button
            variant="admin_primary"
            onClick={() => act("mark_posted", { reference: reference.trim() })}
            data-testid="payout-mark-paid"
          >
            Mark paid
          </Button>
          <Button
            variant="admin_secondary"
            onClick={() => act("mark_failed", { reason: "Marked failed by admin" })}
          >
            Failed
          </Button>
        </div>
      )}
    </div>
  )
}

// what a search matches in a payout: its report number, amount, date, status, references, message
const payoutText = (p, t, locale) =>
  [p.invoiceNumber, money(p.amount, p.currency), (p.amount / 100).toFixed(2), dateOnly(p.postedAt || p.runAt || p.createdAt, locale), t(PAYOUT_LABEL[p.status] || p.status), p.status, p.reference, p.providerPaymentId, p.traceId, p.transferMessage]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/\d+[.,]\d+/g, (n) => `${n} ${n.replace(",", ".")} ${n.replace(".", ",")}`)

export default function PayoutsPanel({ orgId, admin = false, canEdit = false, onOpenDetails }) {
  const t = usePortalTexts(PAYOUTS)
  const locale = useSiteLanguage() === "bg" ? "bg-BG" : "en-GB"
  const [data, setData] = useState(null)
  const [tick, setTick] = useState(0)
  // the payout whose report is open, in the url (?report=<payoutId>): the payout mail's link opens it
  const [reportId, setReportId] = useUrlParam("report", { default: "" })
  // Payouts | Commission (cash on delivery: due, Pay now, the card's charges) | Documents (Aldoo's invoices)
  const [ptab, setPtab] = useUrlParam("ptab", { default: "payouts" })
  const [paying, setPaying] = useState(false)
  const reload = useCallback(() => setTick((n) => n + 1), [])
  // a contract sent, signed or withdrawn elsewhere (the admin, another member, a phone):
  // reloaded at once (aldoo-com-api vendor-submissions broadcast "submissions:contract")
  useSubmissionEvents({ orgId: orgId || null, enabled: !!orgId, events: ["submissions:contract"], onEvent: reload })
  // a payout made, sent, paid or failed (the admin, Stripe), and a sale or refund that moves what
  // is held: reloaded at once (aldoo-na store/broadcast "store:payout", "store:sale", "store:refund").
  // A sale's earnings are written just after it is announced: the reload waits a moment for them.
  const settle = useRef(null)
  useEffect(() => () => clearTimeout(settle.current), [])
  useStoreEvents({
    orgId: orgId || null,
    enabled: !!orgId,
    events: ["store:payout", "store:sale", "store:refund"],
    onEvent: ({ event }) => {
      clearTimeout(settle.current)
      settle.current = setTimeout(reload, event === "store:payout" ? 0 : 1500)
    },
  })

  useEffect(() => {
    let alive = true
    PayoutsOverview(orgId)
      .then((res) => alive && setData(res))
      .catch(() => alive && setData({ error: true }))
    return () => {
      alive = false
    }
  }, [orgId, tick])

  // the open payout, in the url (?report=<payoutId>): the payout mail's link opens it on its report
  const openId = reportId
  const setOpenId = (id) => setReportId(id || "")
  // the payouts with what a search matches in each (DataTable's `searchField`)
  const rows = (data?.payouts || []).map((p) => ({ ...p, _search: payoutText(p, t, locale) }))

  if (!data) return <Empty>{t`Loading…`}</Empty>
  if (data.error) return <LoadError title={t`Could not load the payouts`} onRetry={() => (setData(null), reload())} testId="payouts-load-error" />

  const b = data.balances?.[0] || { currency: "eur", pending: 0, available: 0, paid: 0, nextPayout: 0 }
  const share = Math.round((100 - Number(data.terms?.commissionPercent ?? 0)) * 100) / 100
  // no contract in force yet: the share of the one waiting — sent for signature, or (the admin)
  // made at the approval and sent once the company details are complete — not the default rate
  const waiting = data.terms?.provisional ? data.contract?.pending || (admin ? data.contract?.draft : null) : null
  // the contract's smallest payout (minor units): a smaller balance waits for the next payout
  const minimum = Math.round(Number(data.terms?.minPayout || 0) * 100)
  const unpaid = (b.pending || 0) + (b.available || 0)
  const belowMinimum = minimum > 0 && b.available < minimum

  // money leaves the account: asked first, and one request at a time (the server claims the sales once too)
  const payNow = () =>
    showPromptDialog({
      variant: "admin",
      title: `Pay ${money(b.available, b.currency)} now?`,
      message: `A payout of ${money(b.available, b.currency)} is made for the sales due today${belowMinimum ? ` — below the vendor's ${money(minimum, b.currency)} minimum, paid on purpose` : ""}${data.provider === "stripe" ? ", sent through Stripe to the vendor's bank account" : " — then make the bank transfer and mark it paid"}.`,
      confirmText: "Pay now",
      cancelText: "Cancel",
      onConfirm: sendPayout,
    })
  const sendPayout = async () => {
    if (paying) return
    setPaying(true)
    const res = await PayoutsAdmin("pay_now", { orgId, ...(belowMinimum ? { force: true } : {}) }).catch((e) => ({ error: e?.message }))
    setPaying(false)
    if (res?.error) return showErrorToast(res.error)
    if (!res.paid?.length) {
      // the server's reason keys, read out (each distinct reason once)
      const reasons = [...new Set((res.skipped || []).map((s) => REASON[s.reason] || s.reason))]
      const text = reasons.map((r) => r.charAt(0).toUpperCase() + r.slice(1)).join(", ")
      return showErrorToast("Nothing paid", text || "Nothing available")
    }
    showSuccessToast(`Payout created: ${money(res.paid[0].amount, res.paid[0].currency)}`)
    reload()
  }
  const revealIban = async () => {
    const res = await PayoutsAdmin("reveal_iban", { orgId }).catch(() => null)
    showSuccessToast(res?.iban ? `IBAN: ${res.iban}` : "No IBAN on file (paid through Stripe, or not set)")
  }

  return (
    <div className="flex flex-col gap-4" data-testid="payouts-panel">
      {data.blocker && BLOCKER[data.blocker] && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="payouts-blocker">
          {t(BLOCKER[data.blocker])}{" "}
          {onOpenDetails && (
            <button
              type="button"
              className="underline font-medium"
              onClick={() => onOpenDetails()}
            >
              {t`Company and payout details`} →
            </button>
          )}
        </div>
      )}

      <Tabs
        variant="admin"
        value={["payouts", "commission"].includes(ptab) ? ptab : "payouts"}
        onChange={setPtab}
        styleOverrides={{
          tabInactive: "text-admin_text_secondary hover:text-admin_text bg-admin_neutral/60 hover:bg-admin_paper/70 border-b-2 border-transparent",
        }}
        tabs={[
          { value: "payouts", label: t`Payouts` },
          { value: "commission", label: t`Commission` },
        ]}
        data-testid="payouts-tabs"
      />
      {ptab === "commission" && <CommissionTab orgId={orgId} canEdit={canEdit || admin} locale={locale} />}
            {ptab !== "commission" && (
      <>
      {/* what the vendor will get, large; the rest beside it, smaller (never below zero: what the cash on
          delivery commission comes to beyond the online sales is the Commission tab's) */}
      {/* the payouts' figures are the online sales': never below zero — the cash on delivery commission beyond
          them is the Commission tab's */}
      <NextPayoutHero
        nextPayout={Math.max(0, b.nextPayout || 0)}
        unpaid={unpaid}
        minimum={minimum}
        currency={b.currency}
        nextDate={dateOnly(data.nextPayoutDate, locale)}
        last={(data.payouts || []).find((p) => p.status === "posted")}
        noContract={data.blocker === "no_signed_contract"}
        locale={locale}
        t={t}
        action={
          // only with something due: there is nothing to pay otherwise
          // with a signed contract only: the sales before it are not paid from (aldoo-na payouts/runs plan)
          admin && b.available > 0 && data.blocker !== "no_signed_contract" ? (
            <Button
              variant="admin_primary"
              onClick={payNow}
              disabled={paying}
              data-testid="payouts-pay-now"
            >
              {paying ? "Paying…" : belowMinimum ? `Pay ${money(b.available, b.currency)} anyway (below the minimum)` : `Pay ${money(b.available, b.currency)} now`}
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card label={t`Held (not yet due)`} value={money(Math.max(0, b.pending || 0), b.currency)} sub={t("each sale waits %s days for refunds", data.terms?.holdDays)} testId="payouts-held" helpKey={HELP.held} />
        <Card label={t`Paid to you so far`} value={money(Math.max(0, b.paid || 0), b.currency)} testId="payouts-paid" helpKey={HELP.paid} />
        <Card
          label={t`Your share of sales`}
          value={`${waiting ? Math.round((100 - Number(waiting.commissionPercent)) * 100) / 100 : share}%`}
          sub={
            waiting
              ? waiting.status === "sent"
                ? t`per the contract sent, waiting for signature`
                : t`per the contract sent once the company details are complete`
              : data.terms?.provisional
                ? t`default rate, until your contract is signed`
                : t("Aldoo commission %s%%", data.terms?.commissionPercent)
          }
          testId="payouts-share"
          helpKey={HELP.share}
        />
      </div>

      {admin && (
        <div className="flex flex-wrap gap-2 bg-admin_paper py-2 px-4 rounded-lg border border-admin_bordered">
          {data.provider === "manual" && 
            <Button 
              variant="admin_secondary" 
              onClick={revealIban}
            >
              Show IBAN for the transfer
            </Button>
          }
          <Button 
            variant="admin_secondary" 
            onClick={async () => (await PayoutsAdmin("sync", { orgId }), reload())}
          >
            Recalculate
          </Button>
          <span className="text-xs text-admin_text_secondary self-center">
            Payouts are made {data.provider === "stripe" ? "through Stripe Global Payouts" : "by bank transfer (mark them paid)"}.
          </span>
        </div>
      )}

      <Section title={<span className="inline-flex items-center gap-1.5">{t`Payouts`}<HelpHint helpKey={HELP.list} iconClassName="w-[18px] h-[18px]" /></span>} subtitle={t`The payments Aldoo has sent you for your online sales (paid by card). Cash on delivery sales are paid to you by the courier.`}>
        {data.payouts?.length ? (
          <div data-testid="payouts-list">
            <DataTable
              variant="admin"
              // the search as wide as the rows (the variant leaves room for a title beside it)
              styleOverrides={{ searchInput: "text-admin_text w-full h-auto py-1" }}
              data={rows}
              itemIdField="_id"
              itemsPerPage={10}
              searchField="_search"
              searchPlaceholder={t`Search payouts by report number, amount, date or reference`}
              paginationOfLabel={t`of`}
              // the open payout is a lifted card: no sideways-scroll fades over it
              scrollFades={false}
              mobileCards={false}
              renderRow={(p) => (
                <PayoutItem
                  key={p._id}
                  payout={p}
                  admin={admin}
                  locale={locale}
                  t={t}
                  expanded={openId === String(p._id)}
                  dimmed={!!openId && openId !== String(p._id)}
                  initialTab={openId === String(p._id) && reportId === String(p._id) ? "report" : undefined}
                  onToggle={() => setOpenId(openId === String(p._id) ? "" : String(p._id))}
                  onChanged={reload}
                />
              )}
              noData={<Empty>{t`No payouts match`}</Empty>}
            />
          </div>
        ) : (
          <Empty>{t`No payouts yet`}</Empty>
        )}
      </Section>
      </>
      )}
    </div>
  )
}
